/**
 * Wound Progression Chart Component
 * 
 * Visualizes wound size trends over time with status indicators.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Calendar,
  Loader2,
  AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WoundProgressionChart({ patientId, location, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const loadProgression = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/wound-assessment/progression/${patientId}/${encodeURIComponent(location)}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error("Failed to load progression data");
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId, location]);

  useEffect(() => {
    loadProgression();
  }, [loadProgression]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-400">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  if (!data || !data.progression || data.progression.length === 0) {
    return (
      <div className="text-center p-8 text-slate-400">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p>No progression data available for this wound</p>
      </div>
    );
  }

  const { progression, summary } = data;
  
  // Calculate chart dimensions
  const maxArea = Math.max(...progression.map(p => p.area_sq_cm));
  const chartHeight = 200;
  const chartWidth = Math.min(progression.length * 60, 600);

  // Get trend icon
  const TrendIcon = summary?.overall_trend === "improving" ? TrendingDown : 
                    summary?.overall_trend === "worsening" ? TrendingUp : Minus;
  const trendColor = summary?.overall_trend === "improving" ? "text-green-400" :
                     summary?.overall_trend === "worsening" ? "text-red-400" : "text-slate-400";

  return (
    <div className="space-y-4" data-testid="wound-progression-chart">
      {/* Summary Card */}
      {summary && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-teal-400" />
                Wound Progression Summary
              </span>
              <Badge className={`${
                summary.overall_trend === "improving" ? "bg-green-500/20 text-green-300" :
                summary.overall_trend === "worsening" ? "bg-red-500/20 text-red-300" :
                "bg-slate-600 text-slate-300"
              }`}>
                <TrendIcon className="w-3 h-3 mr-1" />
                {summary.overall_trend.charAt(0).toUpperCase() + summary.overall_trend.slice(1)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Initial Size</p>
                <p className="text-white font-medium">{summary.initial_area_sq_cm} cm²</p>
              </div>
              <div>
                <p className="text-slate-500">Current Size</p>
                <p className="text-white font-medium">{summary.current_area_sq_cm} cm²</p>
              </div>
              <div>
                <p className="text-slate-500">Change</p>
                <p className={`font-medium ${
                  summary.area_change_percent < 0 ? "text-green-400" :
                  summary.area_change_percent > 0 ? "text-red-400" : "text-slate-300"
                }`}>
                  {summary.area_change_percent > 0 ? "+" : ""}{summary.area_change_percent}%
                </p>
              </div>
              <div>
                <p className="text-slate-500">Assessments</p>
                <p className="text-white font-medium">{summary.total_assessments}</p>
              </div>
            </div>
            
            <div className="flex gap-4 mt-4 pt-4 border-t border-slate-700 text-xs">
              <div className="flex items-center gap-1">
                <TrendingDown className="w-3 h-3 text-green-400" />
                <span className="text-slate-400">Improved: {summary.times_improved}x</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-red-400" />
                <span className="text-slate-400">Declined: {summary.times_declined}x</span>
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <Calendar className="w-3 h-3 text-slate-500" />
                <span className="text-slate-500">
                  {new Date(summary.first_assessment).toLocaleDateString()} - {new Date(summary.latest_assessment).toLocaleDateString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visual Chart */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Size Trend (Area in cm²)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative" style={{ height: chartHeight + 40, width: "100%" }}>
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-slate-500 pr-2">
              <span>{maxArea.toFixed(1)}</span>
              <span>{(maxArea / 2).toFixed(1)}</span>
              <span>0</span>
            </div>
            
            {/* Chart area */}
            <div className="ml-10 relative" style={{ height: chartHeight }}>
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="border-t border-slate-700/50 w-full" />
                ))}
              </div>
              
              {/* Bars */}
              <div className="flex items-end gap-1 h-full overflow-x-auto pb-2">
                {progression.map((point, index) => {
                  const barHeight = maxArea > 0 ? (point.area_sq_cm / maxArea) * chartHeight : 0;
                  const barColor = point.comparison === "improved" ? "bg-green-500" :
                                   point.comparison === "declined" ? "bg-red-500" :
                                   point.comparison === "stable" ? "bg-blue-500" : "bg-teal-500";
                  
                  return (
                    <div key={index} className="flex flex-col items-center min-w-[40px]">
                      <div 
                        className={`w-8 ${barColor} rounded-t transition-all hover:opacity-80 cursor-pointer relative group`}
                        style={{ height: Math.max(barHeight, 4) }}
                      >
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                          <div className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs whitespace-nowrap">
                            <p className="text-white">{point.area_sq_cm} cm²</p>
                            <p className="text-slate-400">{point.length_cm} × {point.width_cm} cm</p>
                            <p className="text-slate-500">{new Date(point.date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 mt-1 rotate-45 origin-left">
                        {new Date(point.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex gap-4 mt-4 text-xs justify-center">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span className="text-slate-400">Improved</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500" />
              <span className="text-slate-400">Stable</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span className="text-slate-400">Declined</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-teal-500" />
              <span className="text-slate-400">New/Baseline</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assessment Timeline */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Assessment History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {[...progression].reverse().map((point, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-2 rounded bg-slate-800/50 text-sm"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    point.comparison === "improved" ? "bg-green-500" :
                    point.comparison === "declined" ? "bg-red-500" :
                    point.comparison === "stable" ? "bg-blue-500" : "bg-teal-500"
                  }`} />
                  <span className="text-slate-300">
                    {new Date(point.date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-400">
                    {point.length_cm} × {point.width_cm} cm
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {point.area_sq_cm} cm²
                  </Badge>
                  <Badge className={`text-xs ${
                    point.comparison === "improved" ? "bg-green-500/20 text-green-300" :
                    point.comparison === "declined" ? "bg-red-500/20 text-red-300" :
                    point.comparison === "stable" ? "bg-blue-500/20 text-blue-300" :
                    "bg-slate-600 text-slate-300"
                  }`}>
                    {point.comparison.charAt(0).toUpperCase() + point.comparison.slice(1)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
